import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMicVAD } from "@ricky0123/vad-react";
import amiZeroLogo from "./assets/ami_logo_1x.png";
import {
  CONVERSATION_LANGUAGES,
  ConversationAdapter,
  ConversationContext,
  ConversationHistoryMessage,
  ConversationIdentity,
  ConversationInputMode,
  ConversationLanguage,
  ConversationSpeechReply,
  ConversationSpeechRequest,
  HttpConversationAdapter,
  automaticUserCaptionDurationMs,
  buildConversationRequest,
  bytesToBase64,
  encodeMonoFloat32Wav,
  firstSentence,
  responseIdentity,
  sameConversationIdentity,
  sameConversationOwner,
  speechSegments,
  userCaption,
  vehicleAiCaption,
} from "./conversation-adapter";
import { CarId } from "./protocol";
import { useAppLanguage } from "./i18n";
import { requestViewingAccess } from "./access-ui";
import { guardUnstartedVadDestroyDuringUnmount } from "./vad-cleanup-guard";
import { VenueNoiseDetector } from "./venue-noise-detector";
import {
  createRetainedMicrophoneStreamController,
  setBrowserAudioSessionType,
} from "./retained-microphone-stream";
import { IosReplyAudioGainController } from "./reply-audio-gain";
import "./conversation-panel.css";

type ConversationMode = "microphone-on" | "microphone-off" | "automatic";
type AutoQuestion = { race_time_ms: number; text: string };

function waitForAutomaticUserCaption(text: string, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise(resolve => {
    const finish = () => {
      window.clearTimeout(timer);
      signal.removeEventListener("abort", finish);
      resolve();
    };
    const timer = window.setTimeout(finish, automaticUserCaptionDurationMs(text));
    signal.addEventListener("abort", finish, { once: true });
  });
}

async function loadAutoQuestions(carId: CarId, signal: AbortSignal): Promise<AutoQuestion[]> {
  const assetVersion = "8ff66e3";
  const load = async (scope: "common" | CarId) => {
    const response = await fetch(`/auto-input/${scope}.json?v=${assetVersion}`, {
      signal,
      cache: "no-cache",
    });
    if (!response.ok) throw new Error(`automatic input: HTTP ${response.status}`);
    return (await response.json() as { questions: AutoQuestion[] }).questions;
  };
  return [...await load("common"), ...await load(carId)]
    .sort((left, right) => left.race_time_ms - right.race_time_ms);
}

export interface ConversationCaption {
  identity: ConversationIdentity;
  speaker: "user" | "vehicle-ai";
  text: string;
}

export interface ConversationPanelProps {
  carId: CarId;
  raceTimeMs: number;
  seekGeneration: number;
  context: ConversationContext | null;
  audioMuted?: boolean;
  adapter?: ConversationAdapter;
  onCaptionChange?: (caption: ConversationCaption | null) => void;
  onEngagementChange?: (engaged: boolean) => void;
  onMicrophoneModeChange?: (active: boolean) => void;
  microphoneAttenuationDb?: number;
  onMicrophoneAttenuationChange?: (decibels: number) => void;
  replyAudioGainDb?: number;
  onReplyAudioGainChange?: (decibels: number) => void;
}

function currentIdentity(props: Pick<ConversationPanelProps, "carId" | "raceTimeMs" | "seekGeneration">): ConversationIdentity {
  return { carId: props.carId, raceTimeMs: props.raceTimeMs, seekGeneration: props.seekGeneration };
}

interface ReplyAudio {
  element: HTMLAudioElement;
  release: () => void;
  cancelled: boolean;
}

type SegmentPlaybackResult = "ended" | "recovered-end" | "error" | "aborted";

const REPLY_AUDIO_END_GRACE_MS = 3_000;
const REPLY_AUDIO_MINIMUM_WATCHDOG_MS = 5_000;
const REPLY_AUDIO_UNKNOWN_DURATION_WATCHDOG_MS = 30_000;

function playReplyAudioSegment(replyAudio: ReplyAudio, signal: AbortSignal): Promise<SegmentPlaybackResult> {
  return new Promise(resolve => {
    let settled = false;
    let watchdog: number | null = null;
    const clearWatchdog = () => {
      if (watchdog === null) return;
      window.clearTimeout(watchdog);
      watchdog = null;
    };
    const finish = (result: SegmentPlaybackResult) => {
      if (settled) return;
      settled = true;
      clearWatchdog();
      replyAudio.element.removeEventListener("ended", ended);
      replyAudio.element.removeEventListener("error", failed);
      replyAudio.element.removeEventListener("durationchange", armWatchdog);
      replyAudio.element.removeEventListener("playing", armWatchdog);
      replyAudio.element.removeEventListener("pause", recoverStoppedEnd);
      replyAudio.element.removeEventListener("timeupdate", recoverStoppedEnd);
      signal.removeEventListener("abort", aborted);
      if (result !== "ended") replyAudio.element.pause();
      replyAudio.release();
      resolve(result);
    };
    const ended = () => finish("ended");
    const failed = () => finish("error");
    const aborted = () => finish("aborted");
    const recoverStoppedEnd = () => {
      const { currentTime, duration, ended: mediaEnded, paused } = replyAudio.element;
      if (mediaEnded) finish("ended");
      else if (paused && Number.isFinite(duration) && duration > 0
        && currentTime >= duration - 0.05) finish("recovered-end");
    };
    const armWatchdog = () => {
      if (settled) return;
      clearWatchdog();
      const { currentTime, duration, playbackRate } = replyAudio.element;
      const rate = Math.abs(playbackRate);
      const hasExpectedEnd = Number.isFinite(duration) && duration > 0 && rate > 0;
      const remainingPlaybackMs = hasExpectedEnd
        ? Math.max(0, duration - currentTime) / rate * 1_000
        : REPLY_AUDIO_UNKNOWN_DURATION_WATCHDOG_MS;
      const recoveryDelayMs = hasExpectedEnd
        ? Math.max(REPLY_AUDIO_MINIMUM_WATCHDOG_MS,
          Math.ceil(remainingPlaybackMs) + REPLY_AUDIO_END_GRACE_MS)
        : REPLY_AUDIO_UNKNOWN_DURATION_WATCHDOG_MS;
      watchdog = window.setTimeout(() => finish("recovered-end"), recoveryDelayMs);
    };
    replyAudio.element.addEventListener("ended", ended, { once: true });
    replyAudio.element.addEventListener("error", failed, { once: true });
    replyAudio.element.addEventListener("durationchange", armWatchdog);
    replyAudio.element.addEventListener("playing", armWatchdog);
    replyAudio.element.addEventListener("pause", recoverStoppedEnd);
    replyAudio.element.addEventListener("timeupdate", recoverStoppedEnd);
    signal.addEventListener("abort", aborted, { once: true });
    if (signal.aborted) aborted();
    else {
      armWatchdog();
      void replyAudio.element.play().then(armWatchdog, failed);
    }
  });
}

function audioFromSpeech(
  reply: ConversationSpeechReply,
  element: HTMLAudioElement,
  muted: boolean,
): ReplyAudio | null {
  if (!reply.speech.audio_base64 || reply.speech.audio_mime_type !== "audio/wav") return null;
  const binary = atob(reply.speech.audio_base64);
  const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "audio/wav" }));
  let released = false;
  element.muted = muted;
  element.src = url;
  return {
    element,
    cancelled: false,
    release: () => {
      if (released) return;
      released = true;
      URL.revokeObjectURL(url);
    },
  };
}

export function ConversationPanel(props: ConversationPanelProps) {
  const { language, setLanguage, t } = useAppLanguage();
  const adapter = useMemo(() => props.adapter ?? new HttpConversationAdapter(), [props.adapter]);
  const identityRef = useRef(currentIdentity(props));
  const contextRef = useRef<ConversationContext | null>(props.context);
  const requestRef = useRef<AbortController | null>(null);
  const audioRef = useRef<ReplyAudio | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const audioMutedRef = useRef(props.audioMuted ?? false);
  const audioPrimedRef = useRef(false);
  const replyAudioGainRef = useRef<IosReplyAudioGainController | null>(null);
  const replyAudioGainDbRef = useRef(props.replyAudioGainDb);
  const microphoneStreamControllerRef = useRef(
    createRetainedMicrophoneStreamController(),
  );
  const captionClearTimerRef = useRef<number | null>(null);
  const pendingRef = useRef(false);
  const microphoneRequestedRef = useRef(language !== "ja-JP");
  const languageRef = useRef<ConversationLanguage>(language);
  const languageRevisionRef = useRef(0);
  const languageSwitchingRef = useRef(false);
  const dialogueHistoryRef = useRef<ConversationHistoryMessage[]>([]);
  const historyRaceTimeRef = useRef<number | null>(null);
  const firedAutoQuestionsRef = useRef(new Set<string>());
  const previousRaceTimeRef = useRef(props.raceTimeMs);
  const autoQuestionsOwnerRef = useRef<CarId | null>(null);
  const vadControlRef = useRef<{
    pause: () => Promise<void>;
    pauseAndSubmit: () => Promise<void>;
    start: () => Promise<void>;
    stop: () => Promise<void>;
  } | null>(null);
  const recognitionActiveRef = useRef(false);
  const modeRef = useRef<ConversationMode>(language === "ja-JP" ? "automatic" : "microphone-on");
  const venueNoiseDetectorRef = useRef(new VenueNoiseDetector());
  const venueFallbackRef = useRef(false);
  const discardVadEndRef = useRef(false);
  const pushToTalkPressedRef = useRef(false);
  const pushToTalkCaptureRef = useRef(false);
  const pushToTalkGenerationRef = useRef(0);
  const [pending, setPending] = useState(false);
  const [replyPlaying, setReplyPlaying] = useState(false);
  const [recognitionActive, setRecognitionActive] = useState(false);
  const [microphoneRequested, setMicrophoneRequested] = useState(language !== "ja-JP");
  const [mode, setMode] = useState<ConversationMode>(
    language === "ja-JP" ? "automatic" : "microphone-on",
  );
  const [autoQuestions, setAutoQuestions] = useState<AutoQuestion[]>([]);
  const [venueFallback, setVenueFallback] = useState(false);
  const [pushToTalkPressed, setPushToTalkPressed] = useState(false);
  const [status, setStatus] = useState(t(
    language === "ja-JP" ? "conversation.autoWaiting" : "conversation.vadLoading",
  ));
  const [error, setError] = useState("");

  identityRef.current = currentIdentity(props);
  contextRef.current = props.context;
  languageRef.current = language;
  modeRef.current = mode;
  audioMutedRef.current = props.audioMuted ?? false;
  replyAudioGainDbRef.current = props.replyAudioGainDb;
  const uiText = {
    micOff: t("conversation.micOff"), waiting: t("conversation.waiting"),
    contextSync: t("conversation.contextSync"), vadLoading: t("conversation.vadLoading"),
    vadUnavailable: t("conversation.vadUnavailable"), recognizing: t("conversation.recognizing"),
    transcribing: t("conversation.transcribing"), thinking: t("conversation.thinking"),
    preparing: t("conversation.preparing"), responding: t("conversation.responding"),
    micOn: t("conversation.micOn"), micOffLabel: t("conversation.micOffLabel"),
    autoInput: t("conversation.autoInput"), autoWaiting: t("conversation.autoWaiting"),
    clickLogo: t("conversation.clickLogo"), turnMicOn: t("conversation.turnMicOn"),
    turnMicOff: t("conversation.turnMicOff"), turnAutoInput: t("conversation.turnAutoInput"),
    restartFailed: t("conversation.restartFailed"),
    conversationUnavailable: t("conversation.unavailable"), audioError: t("conversation.audioError"),
    playError: t("conversation.playError"),
    venueNoise: t("conversation.venueNoise"), pushToTalk: t("conversation.pushToTalk"),
    pushToTalkLabel: t("conversation.pushToTalkLabel"),
    holdLogo: t("conversation.holdLogo"),
    returnToNormalMic: t("conversation.returnToNormalMic"),
  };

  const stopReplyAudio = () => {
    if (audioRef.current) audioRef.current.cancelled = true;
    audioRef.current?.element.pause();
    audioRef.current?.release();
    audioRef.current = null;
    setReplyPlaying(false);
  };

  const updateRecognitionActive = (active: boolean) => {
    recognitionActiveRef.current = active;
    setRecognitionActive(active);
  };

  const replyAudioElement = (): HTMLAudioElement => {
    if (!audioElementRef.current) {
      const element = new Audio();
      element.preload = "auto";
      element.setAttribute("playsinline", "");
      audioElementRef.current = element;
    }
    return audioElementRef.current;
  };

  const primeReplyAudioElement = () => {
    if (audioPrimedRef.current) return;
    audioPrimedRef.current = true;
    const element = replyAudioElement();
    if (typeof replyAudioGainDbRef.current === "number") {
      const controller = replyAudioGainRef.current ?? new IosReplyAudioGainController();
      replyAudioGainRef.current = controller;
      controller.setGainDb(replyAudioGainDbRef.current);
      controller.setMuted(audioMutedRef.current);
      controller.enableFromUserGesture(element);
    }
    const silence = encodeMonoFloat32Wav(new Float32Array(800));
    const url = URL.createObjectURL(new Blob([silence], { type: "audio/wav" }));
    const cleanup = () => {
      if (element.src === url) {
        element.pause();
        element.removeAttribute("src");
        element.load();
        element.muted = audioMutedRef.current;
      }
      URL.revokeObjectURL(url);
    };
    // The samples are silent, but the element itself must stay unmuted so iOS
    // grants audible playback to this exact element for the later reply.
    element.muted = false;
    element.volume = 1;
    element.src = url;
    // play() is deliberately invoked in the microphone-button gesture. iOS
    // then keeps this same media element authorized for the asynchronous reply.
    void element.play().then(
      () => window.setTimeout(cleanup, 100),
      () => {
        audioPrimedRef.current = false;
        cleanup();
      },
    );
  };

  useLayoutEffect(() => {
    const muted = props.audioMuted ?? false;
    if (audioElementRef.current) {
      audioElementRef.current.muted = muted;
    }
    replyAudioGainRef.current?.setMuted(muted);
  }, [props.audioMuted]);

  useEffect(() => {
    if (typeof props.replyAudioGainDb === "number") {
      replyAudioGainRef.current?.setGainDb(props.replyAudioGainDb);
    }
  }, [props.replyAudioGainDb]);

  const cancelCaptionClear = () => {
    if (captionClearTimerRef.current === null) return;
    window.clearTimeout(captionClearTimerRef.current);
    captionClearTimerRef.current = null;
  };

  const clearCaptionAfterSpeech = (identity: ConversationIdentity) => {
    cancelCaptionClear();
    captionClearTimerRef.current = window.setTimeout(() => {
      captionClearTimerRef.current = null;
      if (sameConversationOwner(identity, identityRef.current)) props.onCaptionChange?.(null);
    }, 1_000);
  };

  const resetVadForNextTurn = async (): Promise<boolean> => {
    const control = vadControlRef.current;
    if (!control) return false;
    try {
      await control.pause();
      if (microphoneRequestedRef.current && contextRef.current) await control.start();
      return true;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
      setStatus(t("conversation.restartFailed"));
      return false;
    }
  };

  type TurnInput =
    | { kind: "audio"; samples: Float32Array }
    | { kind: "text"; text: string; inputMode: Exclude<ConversationInputMode, "microphone"> };

  const submitInput = async (input: TurnInput) => {
    if (pendingRef.current || audioRef.current) return;
    const identity = identityRef.current;
    const context = contextRef.current;
    const requestLanguage = languageRef.current;
    const languageRevision = languageRevisionRef.current;
    if (!context) {
      setStatus(t("conversation.contextSync"));
      return;
    }
    const requestId = globalThis.crypto.randomUUID();
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    pendingRef.current = true;
    setPending(true);
    setError("");
    try {
      // Keep the microphone suspended from the captured utterance until the
      // answer has finished. Otherwise the synthesized answer can be detected
      // as a new utterance and revoke its own object URL mid-playback.
      await vadControlRef.current?.pause();
      let userText: string;
      let inputMode: ConversationInputMode;
      if (input.kind === "audio") {
        setStatus(t("conversation.transcribing"));
        const transcription = await adapter.requestTranscription({
          schema_version: "stage2-vehicle-ai-audio-request/v1",
          request_id: requestId,
          audio: {
            encoding: "wav",
            sample_rate_hz: 16_000,
            channels: 1,
            base64: bytesToBase64(encodeMonoFloat32Wav(input.samples)),
          },
          conversation_flow: { messages: dialogueHistoryRef.current.slice(-6) },
          conversation_metadata: {
            car_id: identity.carId,
            current_race_time_ms: identity.raceTimeMs,
            seek_generation: identity.seekGeneration,
            locale: requestLanguage,
            input_mode: "microphone",
          },
          race_context: {
            ...context,
            car_id: identity.carId,
            race_time_ms: identity.raceTimeMs,
            seek_generation: identity.seekGeneration,
          },
        }, controller.signal);
        const transcriptionIdentity = responseIdentity(transcription);
        if (controller.signal.aborted || transcription.request_id !== requestId
          || transcription.locale !== requestLanguage || languageRevision !== languageRevisionRef.current
          || !sameConversationIdentity(transcriptionIdentity, identity)
          || !sameConversationOwner(transcriptionIdentity, identityRef.current)) return;
        userText = transcription.text.trim();
        inputMode = "microphone";
      } else {
        userText = input.text.trim();
        inputMode = input.inputMode;
      }
      if (!userText) return;
      if (historyRaceTimeRef.current !== null && identity.raceTimeMs < historyRaceTimeRef.current) {
        dialogueHistoryRef.current = [];
        historyRaceTimeRef.current = null;
      }
      cancelCaptionClear();
      props.onCaptionChange?.({
        identity,
        speaker: "user",
        text: userCaption(userText, requestLanguage),
      });
      setStatus(t("conversation.thinking"));
      const userCaptionReady = inputMode === "automatic"
        ? waitForAutomaticUserCaption(userText, controller.signal)
        : Promise.resolve();

      const reply = await adapter.requestReply(
        buildConversationRequest(
          identity,
          userText,
          inputMode,
          context,
          requestId,
          dialogueHistoryRef.current,
          requestLanguage,
        ),
        controller.signal,
      );
      const replyOwner = responseIdentity(reply);
      if (controller.signal.aborted || reply.request_id !== requestId
        || reply.speech.locale !== requestLanguage || languageRevision !== languageRevisionRef.current
        || !sameConversationIdentity(replyOwner, identity)
        || !sameConversationOwner(replyOwner, identityRef.current)) return;
      const vehicleCaption: ConversationCaption = {
        identity: replyOwner,
        speaker: "vehicle-ai",
        text: vehicleAiCaption(
          replyOwner.carId,
          firstSentence(reply.subtitle.text || reply.answer_text),
          requestLanguage,
        ),
      };
      dialogueHistoryRef.current = [
        ...dialogueHistoryRef.current,
        { role: "user" as const, text: userText },
        { role: "assistant" as const, text: reply.answer_text },
      ].slice(-6);
      historyRaceTimeRef.current = identity.raceTimeMs;

      const segments = speechSegments(reply.speech.text || reply.answer_text, requestLanguage);
      const segmented = segments.length > 1;
      const prepareSegment = (segmentIndex: number) => {
        const speechRequest: ConversationSpeechRequest = {
          schema_version: "stage2-vehicle-ai-speech-request/v1",
          request_id: requestId,
          response_id: reply.response_id,
          car_id: reply.car_id,
          race_time_ms: reply.race_time_ms,
          seek_generation: reply.seek_generation,
          locale: requestLanguage,
          text: segments[segmentIndex],
          ...(segmented ? { segment_index: segmentIndex, segment_count: segments.length } : {}),
        };
        return adapter.requestSpeech(speechRequest, controller.signal).then(
          value => ({ value, reason: null as unknown }),
          reason => ({ value: null, reason }),
        );
      };
      let prepared = prepareSegment(0);
      await userCaptionReady;
      if (controller.signal.aborted || languageRevision !== languageRevisionRef.current
        || !sameConversationOwner(replyOwner, identityRef.current)) return;
      cancelCaptionClear();
      props.onCaptionChange?.(vehicleCaption);
      setStatus(t("conversation.preparing"));
      let speechOwner = replyOwner;
      let naturallyEnded = true;
      setReplyPlaying(true);
      for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex += 1) {
        const result = await prepared;
        if (result.reason) throw result.reason;
        const speechReply = result.value as ConversationSpeechReply;
        speechOwner = responseIdentity(speechReply);
        if (controller.signal.aborted || speechReply.request_id !== requestId
          || speechReply.response_id !== reply.response_id
          || speechReply.speech.locale !== requestLanguage
          || (segmented && (speechReply.segment_index !== segmentIndex
            || speechReply.segment_count !== segments.length))
          || languageRevision !== languageRevisionRef.current
          || !sameConversationIdentity(speechOwner, identity)
          || !sameConversationOwner(speechOwner, identityRef.current)) return;
        // Start synthesizing the following sentence while this one is playing.
        if (segmentIndex + 1 < segments.length) prepared = prepareSegment(segmentIndex + 1);
        if (segmentIndex === 0) setStatus(t("conversation.responding"));
        stopReplyAudio();
        setReplyPlaying(true);
        const replyAudio = audioFromSpeech(
          speechReply,
          replyAudioElement(),
          audioMutedRef.current,
        );
        if (!replyAudio) {
          naturallyEnded = false;
          if (speechReply.speech.error?.message) setError(speechReply.speech.error.message);
          break;
        }
        audioRef.current = replyAudio;
        const playback = await playReplyAudioSegment(replyAudio, controller.signal);
        if (audioRef.current === replyAudio) audioRef.current = null;
        if (playback === "aborted") return;
        if (playback === "error") {
          naturallyEnded = false;
          setError(t("conversation.audioError"));
          break;
        }
      }
      setReplyPlaying(false);
      if (naturallyEnded) clearCaptionAfterSpeech(speechOwner);
      const rearmed = await resetVadForNextTurn();
      if (rearmed && sameConversationOwner(speechOwner, identityRef.current)) {
        setStatus(mode === "automatic" ? t("conversation.autoWaiting")
          : microphoneRequestedRef.current ? t("conversation.waiting") : t("conversation.micOff"));
      }
    } catch (reason) {
      if (!controller.signal.aborted) {
        setError(reason instanceof Error ? reason.message : String(reason));
        const rearmed = await resetVadForNextTurn();
        setStatus(rearmed && microphoneRequestedRef.current
          ? t("conversation.waiting") : t("conversation.unavailable"));
      }
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        pendingRef.current = false;
        setPending(false);
      }
    }
  };

  const submitAudio = (samples: Float32Array) => submitInput({ kind: "audio", samples });
  const submitText = (
    text: string,
    inputMode: Exclude<ConversationInputMode, "microphone"> = "text",
  ) => submitInput({ kind: "text", text, inputMode });

  const restartVadCapture = async () => {
    const control = vadControlRef.current;
    if (!control) return;
    await control.pause();
    if (microphoneRequestedRef.current && contextRef.current
      && !pendingRef.current && !audioRef.current) await control.start();
  };

  const enterVenueFallback = () => {
    if (venueFallbackRef.current || modeRef.current !== "microphone-on") return;
    venueFallbackRef.current = true;
    pushToTalkPressedRef.current = false;
    pushToTalkCaptureRef.current = false;
    pushToTalkGenerationRef.current += 1;
    setVenueFallback(true);
    setPushToTalkPressed(false);
    updateRecognitionActive(false);
    setStatus(t("conversation.venueNoise"));
    void restartVadCapture().catch(reason => {
      setError(reason instanceof Error ? reason.message : String(reason));
    });
  };

  const clearVenueFallback = () => {
    venueFallbackRef.current = false;
    pushToTalkPressedRef.current = false;
    pushToTalkCaptureRef.current = false;
    pushToTalkGenerationRef.current += 1;
    venueNoiseDetectorRef.current.reset();
    setVenueFallback(false);
    setPushToTalkPressed(false);
  };

  const returnFromVenueFallback = (resetDetector = true) => {
    if (!venueFallbackRef.current) return;
    if (resetDetector) clearVenueFallback();
    else {
      venueFallbackRef.current = false;
      pushToTalkPressedRef.current = false;
      pushToTalkCaptureRef.current = false;
      pushToTalkGenerationRef.current += 1;
      setVenueFallback(false);
      setPushToTalkPressed(false);
    }
    updateRecognitionActive(false);
    setStatus(t("conversation.waiting"));
    void restartVadCapture().catch(reason => {
      setError(reason instanceof Error ? reason.message : String(reason));
    });
  };

  const beginPushToTalk = async () => {
    if (!venueFallbackRef.current || pushToTalkPressedRef.current
      || !contextRef.current || pendingRef.current || audioRef.current) return;
    primeReplyAudioElement();
    pushToTalkPressedRef.current = true;
    pushToTalkCaptureRef.current = false;
    setPushToTalkPressed(true);
    setError("");
    setStatus(t("conversation.pushToTalk"));
    const generation = ++pushToTalkGenerationRef.current;
    const control = vadControlRef.current;
    if (!control) return;
    try {
      // Drop any commentary accumulated by ambient monitoring, then start a
      // fresh segment containing only audio captured while the logo is held.
      await control.pause();
      if (!pushToTalkPressedRef.current || !venueFallbackRef.current
        || generation !== pushToTalkGenerationRef.current) return;
      pushToTalkCaptureRef.current = true;
      await control.start();
    } catch (reason) {
      pushToTalkPressedRef.current = false;
      pushToTalkCaptureRef.current = false;
      setPushToTalkPressed(false);
      setError(reason instanceof Error ? reason.message : String(reason));
      setStatus(t("conversation.restartFailed"));
    }
  };

  const endPushToTalk = async (submit = true) => {
    if (!pushToTalkPressedRef.current) return;
    pushToTalkPressedRef.current = false;
    setPushToTalkPressed(false);
    updateRecognitionActive(false);
    pushToTalkGenerationRef.current += 1;
    const captured = pushToTalkCaptureRef.current;
    const control = vadControlRef.current;
    try {
      if (control && captured) {
        if (submit) await control.pauseAndSubmit();
        else await control.pause();
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      pushToTalkCaptureRef.current = false;
    }
    if (venueFallbackRef.current && microphoneRequestedRef.current && contextRef.current
      && !pendingRef.current && !audioRef.current) {
      void control?.start().catch(reason => {
        setError(reason instanceof Error ? reason.message : String(reason));
      });
    }
  };

  const vad = useMicVAD({
    model: "v5",
    startOnLoad: false,
    baseAssetPath: "/",
    onnxWASMBasePath: "/",
    positiveSpeechThreshold: 0.50,
    userSpeakingThreshold: 0.50,
    submitUserSpeechOnPause: true,
    getStream: microphoneStreamControllerRef.current.getStream,
    pauseStream: microphoneStreamControllerRef.current.pauseStream,
    resumeStream: microphoneStreamControllerRef.current.resumeStream,
    onFrameProcessed: probabilities => {
      if (!microphoneRequestedRef.current || modeRef.current !== "microphone-on") return;
      if (pushToTalkPressedRef.current || pendingRef.current || audioRef.current) return;
      const transition = venueNoiseDetectorRef.current.update(probabilities.isSpeech, performance.now());
      if (transition === "enter-push-to-talk") enterVenueFallback();
      else if (transition === "return-to-normal") returnFromVenueFallback(false);
    },
    onSpeechStart: () => {
      if (pendingRef.current || audioRef.current) return;
      if (venueFallbackRef.current && !pushToTalkCaptureRef.current) return;
      stopReplyAudio();
      setError("");
      updateRecognitionActive(true);
      setStatus(t("conversation.recognizing"));
    },
    onSpeechEnd: samples => {
      if (discardVadEndRef.current) return;
      if (venueFallbackRef.current && !pushToTalkCaptureRef.current) return;
      updateRecognitionActive(false);
      if (languageSwitchingRef.current || pendingRef.current || audioRef.current) return;
      void submitAudio(samples);
    },
  });
  const pauseVad = async (submitCurrentSpeech: boolean) => {
    if (submitCurrentSpeech) {
      await vad.pause();
      return;
    }
    discardVadEndRef.current = true;
    try {
      await vad.pause();
    } finally {
      discardVadEndRef.current = false;
    }
  };
  vadControlRef.current = {
    pause: () => pauseVad(false),
    pauseAndSubmit: () => pauseVad(true),
    start: vad.start,
    stop: async () => {
      await pauseVad(false);
      microphoneStreamControllerRef.current.stop();
    },
  };

  useEffect(() => {
    const controller = new AbortController();
    // Invalidate the previous car's schedule before starting the next fetch.
    autoQuestionsOwnerRef.current = null;
    setAutoQuestions([]);
    void loadAutoQuestions(props.carId, controller.signal).then(questions => {
      if (controller.signal.aborted) return;
      autoQuestionsOwnerRef.current = props.carId;
      setAutoQuestions(questions);
    }, reason => {
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : String(reason));
    });
    return () => controller.abort();
  }, [props.carId]);

  useEffect(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    pendingRef.current = false;
    updateRecognitionActive(false);
    cancelCaptionClear();
    stopReplyAudio();
    setPending(false);
    setError("");
    dialogueHistoryRef.current = [];
    historyRaceTimeRef.current = null;
    firedAutoQuestionsRef.current.clear();
    previousRaceTimeRef.current = props.raceTimeMs - 1;
    props.onCaptionChange?.(null);
  }, [props.carId, props.seekGeneration]);

  useEffect(() => {
    if (mode !== "automatic" || language !== "ja-JP" || !props.context || pending
      || autoQuestionsOwnerRef.current !== props.carId) return;
    let previous = previousRaceTimeRef.current;
    if (props.raceTimeMs < previous) {
      firedAutoQuestionsRef.current.clear();
      previous = props.raceTimeMs - 1;
    }
    const question = autoQuestions.find(item => {
      const key = `${item.race_time_ms}:${item.text}`;
      return item.race_time_ms > previous && item.race_time_ms <= props.raceTimeMs
        && !firedAutoQuestionsRef.current.has(key);
    });
    previousRaceTimeRef.current = props.raceTimeMs;
    if (!question) return;
    firedAutoQuestionsRef.current.add(`${question.race_time_ms}:${question.text}`);
    void submitText(question.text, "automatic");
  }, [autoQuestions, language, mode, pending, props.carId, props.context, props.raceTimeMs]);

  useEffect(() => () => {
    requestRef.current?.abort();
    recognitionActiveRef.current = false;
    cancelCaptionClear();
    stopReplyAudio();
    replyAudioGainRef.current?.close();
    replyAudioGainRef.current = null;
    microphoneStreamControllerRef.current.stop();
    audioElementRef.current?.removeAttribute("src");
    audioElementRef.current?.load();
    audioElementRef.current = null;
    audioPrimedRef.current = false;
    setBrowserAudioSessionType("auto");
  }, []);

  useEffect(() => {
    const primeOnFirstClick = (event: MouseEvent) => {
      if (!event.isTrusted) return;
      primeReplyAudioElement();
      document.removeEventListener("click", primeOnFirstClick, true);
    };
    document.addEventListener("click", primeOnFirstClick, true);
    return () => document.removeEventListener("click", primeOnFirstClick, true);
  }, []);

  useEffect(() => guardUnstartedVadDestroyDuringUnmount(), []);

  useEffect(() => {
    props.onEngagementChange?.(microphoneRequested || pending || replyPlaying);
  }, [microphoneRequested, mode, pending, props.onEngagementChange, replyPlaying]);

  useEffect(() => () => props.onEngagementChange?.(false), [props.onEngagementChange]);

  useEffect(() => {
    props.onMicrophoneModeChange?.(microphoneRequested);
  }, [microphoneRequested, props.onMicrophoneModeChange]);

  useEffect(() => () => props.onMicrophoneModeChange?.(false), [props.onMicrophoneModeChange]);

  useEffect(() => {
    if (replyPlaying) setStatus(uiText.responding);
    else if (pending) return;
    else if (!props.context) setStatus(uiText.contextSync);
    else if (venueFallback) setStatus(pushToTalkPressed ? uiText.pushToTalk : uiText.venueNoise);
    else if (recognitionActive) setStatus(uiText.recognizing);
    else if (mode === "automatic") setStatus(uiText.autoWaiting);
    else if (mode === "microphone-off") setStatus(uiText.micOff);
    else if (vad.loading) setStatus(uiText.vadLoading);
    else if (vad.errored) setStatus(uiText.vadUnavailable);
    else setStatus(vad.listening ? uiText.waiting : uiText.vadLoading);
  }, [mode, pending, props.context, pushToTalkPressed, recognitionActive, replyPlaying,
    uiText, vad.errored, vad.listening, vad.loading, venueFallback]);

  useEffect(() => {
    if (!vad.errored) return;
    if (recognitionActiveRef.current) updateRecognitionActive(false);
    clearVenueFallback();
    void vadControlRef.current?.stop();
  }, [vad.errored]);

  useEffect(() => {
    if (props.context || !vad.listening) return;
    const control = vadControlRef.current;
    if (!control) return;
    updateRecognitionActive(false);
    void control.pause().catch(reason => {
      setError(reason instanceof Error ? reason.message : String(reason));
    });
  }, [props.context, vad.listening, vad.pause]);

  useEffect(() => {
    if (!props.context || !microphoneRequestedRef.current || vad.listening
      || vad.loading || vad.errored || pending || replyPlaying) return;
    const control = vadControlRef.current;
    if (!control) return;
    void control.start().catch(reason => {
      microphoneRequestedRef.current = false;
      setMicrophoneRequested(false);
      setMode("microphone-off");
      setError(reason instanceof Error ? reason.message : String(reason));
      setStatus(t("conversation.restartFailed"));
    });
  }, [pending, props.context, replyPlaying, t, vad.errored, vad.listening, vad.loading, vad.start]);

  const selectLanguage = async (nextLanguage: ConversationLanguage) => {
    if (nextLanguage === languageRef.current || languageSwitchingRef.current) return;
    const previousLanguage = languageRef.current;
    const leavingJapanese = previousLanguage === "ja-JP" && nextLanguage !== "ja-JP";
    const enteringJapanese = previousLanguage !== "ja-JP" && nextLanguage === "ja-JP";
    const requestedBeforeSwitch = microphoneRequestedRef.current;
    languageSwitchingRef.current = true;
    languageRevisionRef.current += 1;
    languageRef.current = nextLanguage;
    requestRef.current?.abort();
    requestRef.current = null;
    pendingRef.current = false;
    updateRecognitionActive(false);
    clearVenueFallback();
    cancelCaptionClear();
    stopReplyAudio();
    setPending(false);
    setError("");
    dialogueHistoryRef.current = [];
    historyRaceTimeRef.current = null;
    props.onCaptionChange?.(null);
    if (leavingJapanese) {
      microphoneRequestedRef.current = false;
      setMicrophoneRequested(false);
      setMode("microphone-off");
    } else if (enteringJapanese) {
      microphoneRequestedRef.current = false;
      setMicrophoneRequested(false);
      previousRaceTimeRef.current = props.raceTimeMs - 1;
      setMode("automatic");
    }
    setLanguage(nextLanguage);
    try {
      if (vadControlRef.current) {
        if (leavingJapanese || enteringJapanese) await vadControlRef.current.stop();
        else await vadControlRef.current.pause();
        languageSwitchingRef.current = false;
        if (!leavingJapanese && !enteringJapanese && requestedBeforeSwitch && contextRef.current) {
          microphoneRequestedRef.current = true;
          setMicrophoneRequested(true);
          primeReplyAudioElement();
          await vadControlRef.current.start();
        }
      } else languageSwitchingRef.current = false;
      setStatus(enteringJapanese ? t("conversation.autoWaiting")
        : microphoneRequestedRef.current ? t("conversation.waiting") : t("conversation.micOff"));
    } catch (reason) {
      languageSwitchingRef.current = false;
      await vadControlRef.current?.stop().catch(() => undefined);
      microphoneRequestedRef.current = false;
      setMicrophoneRequested(false);
      setError(reason instanceof Error ? reason.message : String(reason));
      if (enteringJapanese) {
        previousRaceTimeRef.current = props.raceTimeMs - 1;
        setMode("automatic");
        setStatus(t("conversation.autoWaiting"));
      } else {
        setMode("microphone-off");
        setStatus(leavingJapanese ? t("conversation.micOff") : t("conversation.restartFailed"));
      }
    }
  };

  const toggleMicrophone = async () => {
    if (!props.context || pending || replyPlaying) return;
    clearVenueFallback();
    if (mode === "microphone-on") {
      microphoneRequestedRef.current = false;
      setMicrophoneRequested(false);
      updateRecognitionActive(false);
      await vadControlRef.current?.stop();
      setMode("microphone-off");
      setStatus(uiText.micOff);
    } else if (mode === "microphone-off" && language === "ja-JP") {
      microphoneRequestedRef.current = false;
      setMicrophoneRequested(false);
      updateRecognitionActive(false);
      await vadControlRef.current?.stop();
      previousRaceTimeRef.current = props.raceTimeMs - 1;
      setMode("automatic");
      setStatus(uiText.autoWaiting);
    } else {
      if (vad.loading || vad.errored) return;
      primeReplyAudioElement();
      if (!await requestViewingAccess()) return;
      microphoneRequestedRef.current = true;
      setMicrophoneRequested(true);
      try {
        await vadControlRef.current?.start();
        setMode("microphone-on");
        setStatus(uiText.waiting);
      } catch (reason) {
        await vadControlRef.current?.stop().catch(() => undefined);
        microphoneRequestedRef.current = false;
        setMicrophoneRequested(false);
        setMode("microphone-off");
        setError(reason instanceof Error ? reason.message : String(reason));
        setStatus(uiText.restartFailed);
      }
    }
  };

  const nextToggleLabel = mode === "microphone-on" ? uiText.turnMicOff
    : mode === "microphone-off" && language === "ja-JP" ? uiText.turnAutoInput : uiText.turnMicOn;
  const modeLabel = venueFallback ? uiText.pushToTalkLabel : mode === "automatic" ? uiText.autoInput
    : mode === "microphone-on" ? uiText.micOn : uiText.micOffLabel;
  const nextModeNeedsVad = mode === "automatic" || (mode === "microphone-off" && language !== "ja-JP");

  return (
    <section
      className="conversation-panel"
      data-testid="conversation-panel"
      data-car-id={props.carId}
      data-race-time-ms={props.raceTimeMs}
      data-seek-generation={props.seekGeneration}
      data-microphone={vad.listening ? "on" : "off"}
      data-input-mode={mode}
      data-voice-capture-mode={venueFallback ? "push-to-talk" : "automatic-detection"}
      data-conversation-language={language}
      data-context-ready={props.context ? "true" : "false"}
      data-synchronization-rate={props.context?.synchronization.rate_percent.toFixed(3) ?? ""}
      data-tire-thermal-state={props.context?.relative_context.tire_thermal_state.overall ?? ""}
    >
      <div className="conversation-language-selector" role="group" aria-label={t("language.selector")}>
        {CONVERSATION_LANGUAGES.map(item => (
          <button
            type="button"
            key={item.locale}
            lang={item.htmlLang}
            className={`conversation-language-button${language === item.locale ? " selected" : ""}`}
            aria-pressed={language === item.locale}
            onClick={() => void selectLanguage(item.locale)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`ami-zero-logo-button${mode === "microphone-on" ? " microphone-on" : ""}${mode === "automatic" ? " automatic-input" : ""}${venueFallback ? " push-to-talk" : ""}${(venueFallback ? pushToTalkPressed : vad.userSpeaking) ? " speaking" : ""}`}
        aria-label={venueFallback ? uiText.pushToTalk : nextToggleLabel}
        aria-pressed={mode !== "microphone-off"}
        onClick={() => {
          if (!venueFallbackRef.current) void toggleMicrophone();
        }}
        onContextMenu={event => event.preventDefault()}
        onDragStart={event => event.preventDefault()}
        onPointerDown={event => {
          if (!venueFallbackRef.current || event.button !== 0) return;
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          void beginPushToTalk();
        }}
        onPointerUp={event => {
          if (!venueFallbackRef.current) return;
          event.preventDefault();
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          void endPushToTalk();
        }}
        onPointerCancel={() => void endPushToTalk(false)}
        onKeyDown={event => {
          if (!venueFallbackRef.current || event.repeat
            || (event.key !== " " && event.key !== "Enter")) return;
          event.preventDefault();
          void beginPushToTalk();
        }}
        onKeyUp={event => {
          if (!venueFallbackRef.current || (event.key !== " " && event.key !== "Enter")) return;
          event.preventDefault();
          void endPushToTalk();
        }}
        disabled={!props.context || pending || replyPlaying || (nextModeNeedsVad && (vad.loading || Boolean(vad.errored)))}
      >
        <img src={amiZeroLogo} alt="AMI Zero" draggable={false} />
      </button>
      <small className="conversation-logo-credit">©HSE Project</small>
      <p className="conversation-state" aria-live="polite">{status}</p>
      <p className="conversation-microphone">
        <strong>{modeLabel}</strong>
        <span>{venueFallback ? uiText.holdLogo : uiText.clickLogo}</span>
        {venueFallback && (
          <button type="button" className="conversation-normal-mic-button" onClick={() => returnFromVenueFallback()}>
            {uiText.returnToNormalMic}
          </button>
        )}
      </p>
      {typeof props.microphoneAttenuationDb === "number"
        && props.onMicrophoneAttenuationChange && (
        <details className="conversation-volume-calibration">
          <summary>{t("conversation.volumeBalance")}</summary>
          <label>
            <span>{t("conversation.volumeBalanceMicOn")}</span>
            <strong>{props.microphoneAttenuationDb} dB</strong>
            <input
              type="range"
              min="-18"
              max="0"
              step="1"
              value={props.microphoneAttenuationDb}
              aria-label={t("conversation.volumeBalanceMicOn")}
              onChange={event => props.onMicrophoneAttenuationChange?.(
                Number(event.currentTarget.value),
              )}
            />
          </label>
          {typeof props.replyAudioGainDb === "number"
            && props.onReplyAudioGainChange && (
            <label>
              <span>{t("conversation.volumeBalanceReply")}</span>
              <strong>
                {props.replyAudioGainDb > 0 ? "+" : ""}
                {props.replyAudioGainDb} dB
              </strong>
              <input
                type="range"
                min="-12"
                max="6"
                step="1"
                value={props.replyAudioGainDb}
                aria-label={t("conversation.volumeBalanceReply")}
                onChange={event => props.onReplyAudioGainChange?.(
                  Number(event.currentTarget.value),
                )}
              />
            </label>
          )}
          <small>{t("conversation.volumeBalanceHelp")}</small>
        </details>
      )}
      <p className="conversation-caption-note">{t("conversation.mobileCaptionNote")}</p>
      {error && <p className="conversation-error" role="alert">{error}</p>}
      <small className="conversation-voice-credit">VOICEVOX:ナースロボ＿タイプＴ</small>
    </section>
  );
}

export function BigCaption({ caption }: { caption: ConversationCaption | null }) {
  return (
    <div className="big-caption" data-testid="big-caption" aria-live="polite">
      {caption?.text.split(/[\n｜]/).map((line, index) => (
        <span className="big-caption-line" key={`${caption.identity.seekGeneration}-${index}`}>{line.trim()}</span>
      ))}
    </div>
  );
}
